import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import ym from 'react-yandex-metrika';

import b from 'b_';
import cx from 'classnames';
import { useResizeObserver } from 'platform-components/src/utils/hooks';
import { H3, UI_KIT_HEADER_TYPOGRAPHY } from 'platform-components';

import { getHasNoActiveTeams } from 'common.components/user/selectors';
import { SubjectGradeSelector } from 'common.components/subject-grade-selector/subject-grade-selector';

import { ShowcaseMetrikaParams } from '../typings';

import { ContentShowcaseMobileItem } from './__item/__mobile-item/content-showcase-widget__mobile-item';
import { ContentShowcaseItem } from './__item/content-showcase__item';
import { ContentShowcaseMainItem } from './__main-item/content-showcase__main-item';
import { BLOCK_SIZE, ContentShowcaseWidgetData, ContentShowcaseWidgetItemData } from './typings';
import { getBlockSize, getMainBlockSize } from './utils';

import './content-showcase-widget.scss';

export interface ItemContentProps {
    block: unknown;
    mix?: string;
}

interface Props {
    data: ContentShowcaseWidgetData;
    itemContent: (props: ItemContentProps) => JSX.Element;
    metrikaParams?: ShowcaseMetrikaParams;
    onMainBlockTitleClick?: (props: ContentShowcaseWidgetItemData) => void;
    hideSelector?: boolean;
}

const ANIMATION_DURATION = 400;

export const ContentShowcaseWidget = (props: Props) => {
    const { data, itemContent, metrikaParams, onMainBlockTitleClick, hideSelector } = props;
    const { title, items } = data;
    const [blocks, setBlocks] = useState([...items]);
    const [selectedBlock, ...unselected] = blocks;
    const blocksRefs = useRef<Array<HTMLDivElement | null>>([]);
    const selectedBlockRef = useRef<HTMLDivElement | null>(null);
    const [mainBlockSize, setMainBlockSize] = useState<BLOCK_SIZE | null>(null);
    const [blockSize, setBlockSize] = useState<BLOCK_SIZE | null>(null);

    const [animation, setAnimation] = useState<any>(null);

    const shouldShowGradeSelect = useSelector(getHasNoActiveTeams) && !hideSelector;

    const isDesktop = useMedia('(min-width: 769px)');

    const containerRef = useRef<HTMLDivElement>(null);

    const handleResize = () => {
        const mainBlockWidth = selectedBlockRef.current?.offsetWidth;
        if (mainBlockWidth) {
            setMainBlockSize(getMainBlockSize(mainBlockWidth));
        }

        const blockWidth = blocksRefs.current?.[0]?.offsetWidth;
        if (blockWidth) {
            setBlockSize(getBlockSize(blockWidth));
        }
    };

    useResizeObserver(containerRef, handleResize);

    // eslint-disable-next-line complexity
    const handleClickItem = (index: number, subjectSlug?: string) => {
        if (subjectSlug) {
            ym('reachGoal', 'teacher_main_week_click_widget', {
                ...metrikaParams,
                selectedSubjectSlug: subjectSlug,
            });
        }

        const from = selectedBlockRef.current?.getBoundingClientRect();
        const to = blocksRefs.current?.[index]?.getBoundingClientRect();

        setAnimation({
            index,
            item1Top: from?.top || 0,
            item1Left: from?.left || 0,
            item1Height: from?.height || 0,
            item1Width: from?.width || 0,
            item2Top: to?.top || 0,
            item2Left: to?.left || 0,
            item2Height: to?.height || 0,
            item2Width: to?.width || 0,
        });

        setTimeout(() => {
            const newItems = [...blocks];

            newItems[0] = blocks[index + 1];
            newItems[index + 1] = blocks[0];
            setBlocks(newItems);
        }, ANIMATION_DURATION);

        setTimeout(() => {
            setAnimation(null);
        }, ANIMATION_DURATION);
    };

    if (!blocks.length) {
        return null;
    }

    const animationStyles = {
        '--targetLeft': `${animation?.item2Left - animation?.item1Left}px`,
        '--targetTop': `${animation?.item2Top - animation?.item1Top}px`,
        '--targetScaleHeight': animation?.item2Height / animation?.item1Height,
        '--targetScaleWidth': animation?.item2Width / animation?.item1Width,
    } as React.CSSProperties;

    return (
        <div className="content-showcase-widget__wrapper" ref={containerRef}>
            {title && (
                <H3 mix="content-showcase-widget__title" typography={UI_KIT_HEADER_TYPOGRAPHY.headlineMMedium}>
                    {title}
                </H3>
            )}

            {shouldShowGradeSelect && <SubjectGradeSelector />}
            {!isDesktop ? (
                <>
                    <div className="content-showcase-widget">
                        {items.map((item, index) => {
                            return <ContentShowcaseMobileItem ItemContent={itemContent} data={item} key={index} />;
                        })}
                    </div>
                </>
            ) : (
                <>
                    <div className="content-showcase-widget">
                        <div
                            className={cx(
                                b('content-showcase-widget', 'block', { animated: Boolean(animation) }),
                                `first-of-${items.length}`
                            )}
                            ref={selectedBlockRef}
                            style={animation ? animationStyles : undefined}
                        >
                            <ContentShowcaseMainItem
                                ItemContent={itemContent}
                                data={selectedBlock}
                                onTitleClick={onMainBlockTitleClick}
                                size={mainBlockSize}
                            />
                        </div>
                        {unselected.map((item, index) => {
                            const shouldShowSwitchAnimation = animation?.index === index;
                            const animationVars = (
                                shouldShowSwitchAnimation
                                    ? {
                                          '--targetLeft': `${animation?.item1Left - animation?.item2Left}px`,
                                          '--targetTop': `${animation?.item1Top - animation?.item2Top}px`,
                                          '--targetScaleHeight': animation?.item1Height / animation?.item2Height,
                                          '--targetScaleWidth': animation?.item1Width / animation?.item2Width,
                                      }
                                    : {}
                            ) as React.CSSProperties;

                            return (
                                <div
                                    className={b('content-showcase-widget', 'block', {
                                        animated: shouldShowSwitchAnimation,
                                    })}
                                    key={index}
                                    ref={(el: HTMLDivElement) => (blocksRefs.current[index] = el)}
                                    style={{
                                        ...animationVars,
                                    }}
                                >
                                    <ContentShowcaseItem
                                        data={item}
                                        onClick={() => handleClickItem(index)}
                                        size={blockSize}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
